import { type ReactNode, useEffect } from "react";
import { useField } from "formik";
import { Flex, HelpIndicator, Label, LoadingIndicator, Text } from "@jsq/ds/core";
import { FormDatePicker, FormSelect, FormTextInput, useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { AIFieldLabel } from "~/pages/Compliance/Common/AIFieldLabel";
import { useAIPopulatedFields } from "~/pages/Compliance/Common/hooks/useAIPopulatedFields";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { isJointAccountType } from "~/pages/Compliance/utils/accountTypeUtils";
import { getAmlEntityTypeOptions } from "~/pages/Compliance/utils/amlTypeIdUtil";
import { FormValues } from "../../OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/types";
import AddressFields from "../AddressFields";
import { useCountryOptions } from "../AddressFields/CountryFormField/useCountryOptions";
import { AutoProfileCreationCard } from "../AutoProfileCreationCard";
import { NATURE_OF_BUSINESS_OPTIONS } from "./natureOfBusiness";
import { EMPTY_PRODUCT_LINE_DISPLAY, PRODUCT_LINE_HELP_TEXT } from "./productLine";
import {
  equalWidthColumn,
  fieldSpacing,
  helperText,
  productLineLabelRow,
} from "./styles_vanilla.css";
import { useEntityAccountTypeOptions } from "./useEntityAccountTypeOptions";

const EntityTypeSelect = ({ accountTypes }: { accountTypes: string[] }) => (
  <FormSelect name="entityType" label="Entity type" placeholder="Select entity type" full>
    <FormSelect.Section>
      {accountTypes.map((accountType) => (
        <FormSelect.Option key={accountType} value={accountType}>
          {accountType}
        </FormSelect.Option>
      ))}
    </FormSelect.Section>
  </FormSelect>
);

const AmlEntityTypeSelect = ({ entityType }: { entityType?: string }) => {
  const options = getAmlEntityTypeOptions(entityType);
  const isResolved = Boolean(entityType) && options.length === 1;

  return (
    <>
      <FormSelect
        name="amlEntityType"
        label="AML entity type"
        placeholder={entityType ? "Select AML entity type" : "Choose an entity type first"}
        full
        disabled={!entityType || isResolved}
      >
        <FormSelect.Section>
          {options.map((amlEntityType) => (
            <FormSelect.Option key={amlEntityType} value={amlEntityType}>
              {amlEntityType}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
      <Text variant="secondary-sm" className={helperText}>
        Based on selected entity type
      </Text>
    </>
  );
};

const EntityDetailsFields = ({
  editMode,
  aiExtractionSupported,
  taxIdWarning,
}: {
  editMode: boolean;
  aiExtractionSupported: boolean;
  taxIdWarning?: ReactNode;
}) => {
  const { hasFieldPopulatedByAI } = useAIPopulatedFields<FormValues>({
    editMode,
    fieldNames: ["entityName", "dateOfFormation"],
    aiExtractionSupported,
  });

  return (
    <>
      <div className={fieldSpacing}>
        <FormTextInput
          label={
            <AIFieldLabel label="Entity name" showIcon={hasFieldPopulatedByAI("entityName")} />
          }
          name="entityName"
          data-testid="entity-name"
          isFullWidth
        />
      </div>
      <Flex gap={Sizes.MD} className={fieldSpacing}>
        <div>
          <FormTextInput name="taxId" label="EIN" />
          {taxIdWarning}
        </div>
        <div data-testid="formation-date">
          <Flex flexDirection="row" alignItems="center">
            <AIFieldLabel
              label="Formation Date"
              showIcon={hasFieldPopulatedByAI("dateOfFormation")}
            />
          </Flex>
          <FormDatePicker maxDate={new Date()} name="dateOfFormation" />
        </div>
      </Flex>
    </>
  );
};

const ProductLineField = ({ productLine }: { productLine?: string }) => (
  <div className={fieldSpacing} data-testid="product-line-field">
    <Flex alignItems="center" gap={Sizes.XS} className={productLineLabelRow}>
      <Label>Product line</Label>
      <HelpIndicator id="product-line-help" content={PRODUCT_LINE_HELP_TEXT}>
        <Icon
          type="question-circle"
          size="small"
          variant="outline"
          color={Colors.Neutral600}
          aria-label="Product line help"
        />
      </HelpIndicator>
    </Flex>
    <Text variant="regular-md" data-testid="product-line-value">
      {productLine || EMPTY_PRODUCT_LINE_DISPLAY}
    </Text>
  </div>
);

const PrincipalPlaceOfBusinessSelect = () => {
  const { loading, countryOptions } = useCountryOptions();

  if (loading) return <LoadingIndicator />;

  return (
    <FormSelect
      name="principalPlaceOfBusiness"
      label="Principal place of business"
      placeholder="Select country"
      full
      className={fieldSpacing}
      data-testid="principal-place-of-business"
    >
      <FormSelect.Section>
        {countryOptions.map((country) => (
          <FormSelect.Option key={country.id} value={String(country.id)}>
            {country.name}
          </FormSelect.Option>
        ))}
      </FormSelect.Section>
    </FormSelect>
  );
};

const EntityTypeAndAmlSelects = ({
  accountTypes,
  entityType,
}: {
  accountTypes: string[];
  entityType?: string;
}) => (
  <Flex gap={Sizes.MD} alignItems="flex-start" className={fieldSpacing}>
    <Flex flexGrow={1} flexShrink={1} flexBasis={0} className={equalWidthColumn}>
      <EntityTypeSelect accountTypes={accountTypes} />
    </Flex>
    <Flex
      flexGrow={1}
      flexShrink={1}
      flexBasis={0}
      flexDirection="column"
      className={equalWidthColumn}
    >
      <AmlEntityTypeSelect entityType={entityType} />
    </Flex>
  </Flex>
);

const OnboardingEntityCoreFields = ({
  accountTypes,
  entityType,
  taxIdWarning,
}: {
  accountTypes: string[];
  entityType?: string;
  taxIdWarning?: ReactNode;
}) => (
  <>
    <div className={fieldSpacing}>
      <FormTextInput label="Name" name="entityName" data-testid="entity-name" isFullWidth />
    </div>
    <EntityTypeAndAmlSelects accountTypes={accountTypes} entityType={entityType} />
    <Flex gap={Sizes.MD} className={fieldSpacing}>
      <Flex flexGrow={1} flexShrink={1} flexBasis={0} className={equalWidthColumn}>
        <FormTextInput name="taxId" label="EIN" />
        {taxIdWarning}
      </Flex>
      <Flex
        flexGrow={1}
        flexShrink={1}
        flexBasis={0}
        flexDirection="column"
        className={equalWidthColumn}
        data-testid="formation-date"
      >
        <Text variant="strong-md" marginBottom="xs">
          Formation date
        </Text>
        <FormDatePicker
          maxDate={new Date()}
          name="dateOfFormation"
          hasIcon
          placeholderText="MM/DD/YYYY"
          isFull
        />
      </Flex>
    </Flex>
    <div className={fieldSpacing}>
      <FormTextInput
        name="registrationNumber"
        label="Registration number"
        data-testid="registration-number"
        isFullWidth
      />
    </div>
    <AddressFields variant="onboarding" />
  </>
);

const OnboardingBusinessFields = () => (
  <Flex gap={Sizes.MD} className={fieldSpacing}>
    <Flex flexGrow={1} flexShrink={1} flexBasis={0} className={equalWidthColumn}>
      <FormSelect
        name="natureOfBusiness"
        label="Nature of business"
        placeholder="Select a type"
        full
        data-testid="nature-of-business"
      >
        <FormSelect.Section>
          {NATURE_OF_BUSINESS_OPTIONS.map((option) => (
            <FormSelect.Option key={option} value={option}>
              {option}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
    </Flex>
    <Flex flexGrow={1} flexShrink={1} flexBasis={0} className={equalWidthColumn}>
      <PrincipalPlaceOfBusinessSelect />
    </Flex>
  </Flex>
);

const OnboardingRootFields = ({
  accountTypes,
  entityType,
  taxIdWarning,
  productLine,
}: {
  accountTypes: string[];
  entityType?: string;
  taxIdWarning?: ReactNode;
  productLine?: string;
}) => (
  <>
    <OnboardingEntityCoreFields
      accountTypes={accountTypes}
      entityType={entityType}
      taxIdWarning={taxIdWarning}
    />
    <ProductLineField productLine={productLine} />
    <OnboardingBusinessFields />
  </>
);

interface EntityFormFieldsProps {
  isEditMode?: boolean;
  inAiExtractionMode?: boolean;
  aiExtractionSupported?: boolean;
  taxIdWarning?: ReactNode;
  /** Entity/client onboarding root-profile layout (Figma Initial-6+). */
  showOnboardingRootFields?: boolean;
  /** Owner/controller entity form for onboarding cases (Figma Add entity) — no product line. */
  showOnboardingOwnerControllerFields?: boolean;
  /** Nature of business + principal place of business on EO/CO edit (same fields as create). */
  showOnboardingBusinessFields?: boolean;
  /** Product line from main (linked subject). Read-only; empty when unlinked. */
  productLine?: string;
}

const EntityFormFields = ({
  isEditMode = false,
  inAiExtractionMode = false,
  aiExtractionSupported = false,
  taxIdWarning,
  showOnboardingRootFields = false,
  showOnboardingOwnerControllerFields = false,
  showOnboardingBusinessFields = false,
  productLine,
}: EntityFormFieldsProps) => {
  const [{ value: entityType }] = useField<string | undefined>("entityType");
  const [{ value: amlEntityType }] = useField<string | undefined>("amlEntityType");
  const { setFieldValue } = useFormContext<FormValues>();
  const { accountTypes, viewableToAccountType } = useEntityAccountTypeOptions();
  const ioReusableProfilesEnabled = useIOReusableProfiles();

  useEffect(() => {
    if (
      !showOnboardingRootFields &&
      !showOnboardingOwnerControllerFields &&
      !ioReusableProfilesEnabled
    ) {
      return;
    }
    const options = getAmlEntityTypeOptions(entityType);
    if (entityType && options.length === 1) {
      if (amlEntityType !== options[0]) {
        void setFieldValue("amlEntityType", options[0]);
      }
    } else if (amlEntityType && !options.includes(amlEntityType)) {
      void setFieldValue("amlEntityType", "");
    }
  }, [
    showOnboardingRootFields,
    showOnboardingOwnerControllerFields,
    ioReusableProfilesEnabled,
    entityType,
    amlEntityType,
    setFieldValue,
  ]);

  if (showOnboardingRootFields) {
    return (
      <OnboardingRootFields
        accountTypes={accountTypes}
        entityType={entityType}
        taxIdWarning={taxIdWarning}
        productLine={productLine}
      />
    );
  }

  if (showOnboardingOwnerControllerFields) {
    return (
      <OnboardingEntityCoreFields
        accountTypes={accountTypes}
        entityType={entityType}
        taxIdWarning={taxIdWarning}
      />
    );
  }

  const shouldHideDetails = aiExtractionSupported && inAiExtractionMode && !entityType;

  const showAutoProfileCreationCard =
    aiExtractionSupported &&
    inAiExtractionMode &&
    !isEditMode &&
    !isJointAccountType(entityType ? viewableToAccountType[entityType] : undefined);

  return (
    <>
      {isEditMode && (
        <Text variant="h4" marginBottom="md">
          Entity description
        </Text>
      )}
      {ioReusableProfilesEnabled ? (
        <EntityTypeAndAmlSelects accountTypes={accountTypes} entityType={entityType} />
      ) : (
        <div className={fieldSpacing}>
          <EntityTypeSelect accountTypes={accountTypes} />
        </div>
      )}
      {!shouldHideDetails && (
        <>
          {showAutoProfileCreationCard && <AutoProfileCreationCard />}
          <EntityDetailsFields
            editMode={isEditMode}
            aiExtractionSupported={aiExtractionSupported}
            taxIdWarning={taxIdWarning}
          />

          {showOnboardingBusinessFields && (
            <div className={fieldSpacing}>
              <FormTextInput
                name="registrationNumber"
                label="Registration number"
                data-testid="registration-number"
                isFullWidth
              />
            </div>
          )}
          <AddressFields editMode={isEditMode} aiExtractionSupported={aiExtractionSupported} />
          {showOnboardingBusinessFields && <OnboardingBusinessFields />}
        </>
      )}
    </>
  );
};

export default EntityFormFields;
